import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';

import { AuditLogUIViewModel } from '../../services/backendServices/ViewModels';
import { IAuditLogProps } from './AuditLogLegacy';

interface AuditLogV2Row {
    id: string;
    timestamp: string;
    user: string;
    parameterName: string;
    previousValue: string;
    newValue: string;
    additionalDetails: Array<{ label: string; value: string }>;
}

function stringifyValues(values: Array<string | number | boolean | null>) {
    return values
        .filter((value) => value !== null)
        .map(String)
        .join(', ');
}

function flattenAuditLogData(
    logData: Map<string, AuditLogUIViewModel>,
    extractAdditionalDetails: IAuditLogProps['extractAdditionalDetails']
): AuditLogV2Row[] {
    const rows: AuditLogV2Row[] = [];

    logData.forEach((group, groupId) => {
        Object.entries(group.EntityData).forEach(([parameterName, change], changeIndex) => {
            rows.push({
                id: `${groupId}-${changeIndex}`,
                timestamp: format(new Date(group.CreatedTime), "yyyy-MM-dd hh:mm:ss a 'PT'"),
                user: group.UserName?.trim() || 'Unknown user',
                parameterName,
                previousValue: stringifyValues(change.OldValue),
                newValue: stringifyValues(change.NewValue),
                additionalDetails: extractAdditionalDetails?.(group) ?? [],
            });
        });
    });

    return rows;
}

function renderValueCell(params: GridRenderCellParams<string>) {
    const value = params.value ?? '';

    return (
        <Box
            component="span"
            sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                color: value ? (params.field === 'previousValue' ? 'error.500' : 'primary.main') : 'text.secondary',
            }}
        >
            {value || '—'}
        </Box>
    );
}

export function AuditLogV2({ logData, extractAdditionalDetails }: IAuditLogProps) {
    const rows = useMemo(
        () => flattenAuditLogData(logData, extractAdditionalDetails),
        [logData, extractAdditionalDetails]
    );
    const [pageSize, setPageSize] = useState(25);
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const selectedRow = useMemo(() => rows.find((row) => row.id === selectedRowId) ?? null, [rows, selectedRowId]);

    const columns = useMemo<GridColumns<AuditLogV2Row>>(
        () => [
            {
                field: 'timestamp',
                headerName: 'Timestamp (PT)',
                minWidth: 205,
                flex: 0.9,
            },
            {
                field: 'user',
                headerName: 'User',
                minWidth: 140,
                flex: 0.8,
            },
            {
                field: 'parameterName',
                headerName: 'Parameter Name',
                minWidth: 200,
                flex: 1.2,
            },
            {
                field: 'previousValue',
                headerName: 'Previous Value',
                minWidth: 220,
                flex: 1.4,
                renderCell: renderValueCell,
            },
            {
                field: 'newValue',
                headerName: 'New Value',
                minWidth: 220,
                flex: 1.4,
                renderCell: renderValueCell,
            },
        ],
        []
    );

    return (
        <Box
            data-testid="audit-log-component"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'min(68vh, 720px)',
                minHeight: 360,
            }}
        >
            <Box
                data-testid="audit-log-v2-grid"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'grey.100',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                    },
                    '& .MuiDataGrid-cell': {
                        alignItems: 'center',
                    },
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    rowsPerPageOptions={[25, 50, 100]}
                    density="compact"
                    disableColumnFilter
                    selectionModel={selectedRowId ? [selectedRowId] : []}
                    onRowClick={(params) => setSelectedRowId(String(params.id))}
                    hideFooterSelectedRowCount
                    localeText={{
                        noRowsLabel: 'No audit history',
                    }}
                />
            </Box>
            {selectedRow && (
                <Box
                    data-testid="audit-log-v2-row-detail"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        border: '1px solid',
                        borderTop: 0,
                        borderColor: 'divider',
                        overflow: 'auto',
                        p: 2,
                    }}
                >
                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">{selectedRow.parameterName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedRow.timestamp} · {selectedRow.user}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="Close audit row details"
                            size="small"
                            onClick={() => setSelectedRowId(null)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Previous Value
                            </Typography>
                            <Typography
                                component="pre"
                                variant="body2"
                                sx={{
                                    m: 0,
                                    color: 'error.500',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {selectedRow.previousValue || '—'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                New Value
                            </Typography>
                            <Typography
                                component="pre"
                                variant="body2"
                                sx={{
                                    m: 0,
                                    color: 'primary.main',
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {selectedRow.newValue || '—'}
                            </Typography>
                        </Box>
                    </Box>
                    {selectedRow.additionalDetails.length > 0 && (
                        <Stack spacing={2} my={2}>
                            <Typography variant="subtitle1">Additional Details</Typography>
                            {selectedRow.additionalDetails.map((detail) => (
                                <Stack direction="row" spacing={1}>
                                    <Typography variant="body2" color="text.secondary">
                                        {detail.label}:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {detail.value}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </Box>
            )}
        </Box>
    );
}
